// InspirationSpace.jsx - Version améliorée avec fil d'Ariane
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom'; 
import { 
    Search, 
    Sparkles, 
    Globe, 
    Lightbulb, 
    BookOpen, 
    Settings, 
    Filter, 
    CheckCircle, 
    ArrowRight, 
    Database, 
    Target, 
    Shield, 
    Star,
    Clock,
    Languages,
    Compass,
    MessageCircle
  } from 'lucide-react'; 

// Importer l'intégrateur d'analyse sémantique
import IntentAnalysisIntegrator from './IntentAnalysisIntegrator';
import userPreferencesManager from '../components/UserPreferencesManager';
import { auth } from '../firebase';

// Composant de carte similaire à ObjectifCard de la landing page
const PhaseCard = ({ number, title, description, icon, isActive, onClick }) => {
  const IconComponent = () => {
    switch(icon) {
      case 'search': return <Search className="h-6 w-6 text-gray-600" />;
      case 'sparkles': return <Sparkles className="h-6 w-6 text-gray-600" />;
      case 'lightbulb': return <Lightbulb className="h-6 w-6 text-gray-600" />;
      case 'message': return <MessageCircle className="h-6 w-6 text-gray-600" />;
      default: return <Search className="h-6 w-6 text-gray-600" />;
    }
  };

  return (
    <div 
      className={`relative bg-white rounded-xl shadow-md overflow-hidden border ${isActive ? 'border-purple-300' : 'border-gray-200'} hover:shadow-lg hover:border-purple-300 transition-all duration-300 cursor-pointer`}
      onClick={onClick}
    >
      <div className="absolute top-4 left-4 w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center border border-purple-200">
        <span className="text-purple-600 font-semibold">{number}</span>
      </div>
      <div className="p-6 pt-14">
        <div className="mb-4 flex justify-center">
          <div className="p-3 bg-gray-100 rounded-full">
            <IconComponent />
          </div>
        </div>
        <h3 className="text-xl font-semibold text-gray-800 mb-2 text-center">{title}</h3>
        <p className="text-gray-600 text-center">{description}</p>
      </div>
      {isActive && (
        <div className="bg-purple-100 py-2 px-4 text-center">
          <span className="text-sm font-medium text-purple-700">Phase active</span>
        </div>
      )}
    </div>
  );
};

// Composant de carte d'avantages similaire à la landing page
const AdvantageCard = ({ title, icon, gradient, color, bgColor, borderHover, features }) => {
  return (
    <div className={`bg-white rounded-xl shadow-md overflow-hidden border border-gray-200 transition-all duration-300 ${borderHover} hover:shadow-lg`}>
      <div className={`bg-gradient-to-r ${gradient} p-6 text-center`}>
        {icon}
        <h3 className="text-xl font-semibold text-gray-800 mt-3">
          {title}
        </h3>
      </div>
      <div className="p-6">
        <ul className="space-y-3">
          {features.map((feature, index) => (
            <li key={index} className="flex items-start">
              <div className={`flex-shrink-0 h-5 w-5 rounded-full ${bgColor} flex items-center justify-center mt-0.5`}>
                <span className={`${color} text-xs`}>✓</span>
              </div>
              <span className="ml-3 text-gray-700">{feature}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

const InspirationSpace = () => {
  const { competenceId } = useParams();
  const userId = auth?.currentUser?.uid || null;
  const location = useLocation();
  const [query, setQuery] = useState('');
  const [objective, setObjective] = useState('');
  const [isAdvancedOptionsOpen, setIsAdvancedOptionsOpen] = useState(false);
  const [activePhase, setActivePhase] = useState(0);
  const navigate = useNavigate();
  const [searchStep, setSearchStep] = useState(1);
  const [hasSemanticAnalysis, setHasSemanticAnalysis] = useState(false);

  // Vérification de l'ID de compétence
  useEffect(() => {
    if (!competenceId) {
      console.error("Aucun ID de compétence n'a été trouvé dans l'URL");
    }
  }, [competenceId]);
  
  // Détecter si nous avons des résultats d'analyse sémantique
  useEffect(() => {
    if (location.state?.searchConfig) {
      setQuery(location.state.searchConfig.query || '');
      setObjective(location.state.searchConfig.objective || '');
      setHasSemanticAnalysis(true);
    } else if (location.state?.semanticAnalysis) {
      setQuery(location.state.optimizedQuery || '');
      setObjective(location.state.semanticAnalysis.primaryGoal || '');
      setHasSemanticAnalysis(true);
    }
  }, [location.state]);
  
  // Persister l'objectif courant si competenceId présent
  useEffect(() => {
    const persistObjectiveFromRoute = async () => {
      try {
        if (!competenceId || !userId) return;
        await userPreferencesManager.setCurrentObjective(userId, {
          id: competenceId,
          title: null,
          theme: null,
          chapitre: null
        });
        await userPreferencesManager.updateLastAccessed(userId);
      } catch (e) {
        console.error('InspirationSpace: erreur persistance objectif courant:', e);
      }
    };
    persistObjectiveFromRoute();
  }, [competenceId, userId]);

  // Détecter la phase active basée sur l'URL actuelle
  useEffect(() => {
    const path = location.pathname;
    
    console.log("URL actuelle:", path);
    
    if (path.includes('/intent-analysis/')) {
      console.log("Détection phase Orientation");
      setActivePhase(1);
    } else if (path.includes('/webSource/')) {
      console.log("Détection phase Recherche 360°");
      setActivePhase(2);
    } else if (path.includes('/webSearch/')) {
      console.log("Détection phase Synthèse");
      setActivePhase(3);
    } else if (path.includes('/inspiration/')) {
      console.log("Sur la page d'inspiration (accueil)");
      setActivePhase(0);
    }
  }, [location.pathname]);
  
  // Définition dynamique des phases
  const getPhasesWithActiveState = () => {
    return [
      {
        number: 1,
        title: "Orientation",
        description: "Préciser votre objectif pour une recherche ciblée et pertinente",
        icon: "message",
        isActive: activePhase === 1
      },
      {
        number: 2,
        title: "Recherche 360°",
        description: "Identifiez et accédez aux meilleures sources d'information pour votre objectif",
        icon: "search",
        isActive: activePhase === 2
      },
      {
        number: 3,
        title: "Synthèse",
        description: "Transformez ces informations en actions concrètes pour atteindre votre objectif",
        icon: "sparkles",
        isActive: activePhase === 3
      }
    ];
  };
  
  // Avantages clés
  const avantages = [
    {
      title: "Smart Orientation",
      icon: <MessageCircle className="h-12 w-12 text-teal-600 mx-auto" />,
      gradient: "from-teal-100 to-teal-50",
      color: "text-teal-600",
      bgColor: "bg-teal-100",
      borderHover: "hover:border-teal-300",
      features: [
        "Compréhension fine de vos objectifs",
        "Détection automatique de l'intention",
        "Suggestions de termes de recherche optimisés"
      ]
    },
    {
      title: "Sources Mondiales",
      icon: <Globe className="h-12 w-12 text-blue-600 mx-auto" />,
      gradient: "from-blue-100 to-blue-50",
      color: "text-blue-600",
      bgColor: "bg-blue-100",
      borderHover: "hover:border-blue-300",
      features: [
        "Accédez à des ressources du monde entier",
        "Sélection par localisation géographique",
        "Diversité des perspectives et approches"
      ]
    },
    {
      title: "Qualité & Transparence",
      icon: <Star className="h-12 w-12 text-purple-600 mx-auto" />,
      gradient: "from-purple-100 to-purple-50",
      color: "text-purple-600",
      bgColor: "bg-purple-100",
      borderHover: "hover:border-purple-300",
      features: [
        "Sources non sponsorisées de haute qualité",
        "Analyse de l'intention par IA pour pertinence",
        "Classification par fiabilité et popularité"
      ]
    },
    {
      title: "Multilingue Intégral",
      icon: <Languages className="h-12 w-12 text-green-600 mx-auto" />,
      gradient: "from-green-100 to-green-50",
      color: "text-green-600",
      bgColor: "bg-green-100",
      borderHover: "hover:border-green-300",
      features: [
        "Aucune limite en termes de langue",
        "Requêtes et résultats dans votre langue",
        "Traduction intelligente des contenus pertinents"
      ]
    }
  ];
  
  const handlePhaseClick = (phaseNumber) => {
    setActivePhase(phaseNumber);
    
    if (!competenceId) {
      console.error("Impossible de naviguer : ID de l'objectif manquant");
      return;
    }
    
    if (phaseNumber === 1) {
      navigate(`/intent-analysis/${competenceId}`, {
        state: { 
          objective: objective || "Trouver les meilleures ressources pour mon projet",
          returnPath: `/webSource/${competenceId}`
        }
      });
    } else if (phaseNumber === 2) {
      setSearchStep(1);
      navigate(`/webSource/${competenceId}`, {
        state: location.state
      });
    } else if (phaseNumber === 3) {
      setSearchStep(2);
      navigate(`/webSearch/${competenceId}`, {
        state: location.state
      });
    }
  };
    
  const renderSearchResults = () => (
    <div className="bg-white rounded-xl shadow-md overflow-hidden mb-8 border border-gray-200">
      <div className="bg-purple-600 p-4 text-white">
        <div className="flex justify-between items-center">
          <h3 className="text-lg font-semibold">Résultats pour "{query}"</h3>
          <button 
            className="text-purple-100 hover:text-white text-sm flex items-center"
            onClick={() => handlePhaseClick(1)}
          >
            <Filter className="h-4 w-4 mr-1" />
            Modifier la recherche
          </button>
        </div>
        <p className="text-purple-100 text-sm mt-1">Objectif: {objective}</p>
      </div>
      
      <div className="p-5 border-b">
        <div className="flex justify-between items-center mb-4">
          <div className="flex items-center">
            <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center mr-3">
              <CheckCircle className="h-5 w-5 text-green-600" />
            </div>
            <div>
              <h4 className="font-medium text-gray-900">Analyse IA terminée</h4>
              <p className="text-sm text-gray-500">Sources analysées et classées selon vos critères</p>
            </div>
          </div>
          <span className="text-sm text-gray-500 flex items-center"><Clock className="h-4 w-4 mr-1" /> 3 secondes</span>
        </div>
        
        <div className="flex flex-wrap gap-2 mb-3">
          <span className="bg-blue-100 text-blue-800 text-xs rounded-full px-2.5 py-1">16 sources trouvées</span>
          <span className="bg-green-100 text-green-800 text-xs rounded-full px-2.5 py-1">7 éducatives</span>
          <span className="bg-yellow-100 text-yellow-800 text-xs rounded-full px-2.5 py-1">4 gouvernementales</span>
          <span className="bg-purple-100 text-purple-800 text-xs rounded-full px-2.5 py-1">5 commerciales</span>
        </div>
      </div>
      
      <div className="overflow-auto max-h-96">
        <div className="p-4 border-b hover:bg-gray-50 cursor-pointer">
          <div className="flex items-start">
            <div className="h-10 w-10 rounded bg-blue-100 flex items-center justify-center mr-3 mt-1">
              <BookOpen className="h-5 w-5 text-blue-600" />
            </div>
            <div className="flex-1">
              <div className="flex justify-between">
                <h4 className="font-medium text-gray-900">Introduction à la programmation orientée objet</h4>
                <div className="flex">
                  <span className="bg-green-100 text-green-800 text-xs rounded-full px-2 py-0.5 flex items-center mr-1">
                    <Star className="h-3 w-3 mr-0.5" /> 4.8
                  </span>
                </div>
              </div>
              <p className="text-sm text-gray-600 line-clamp-2 mt-1">Un guide complet sur les principes de la POO, avec des exemples pratiques en Java et en Python.</p>
              <div className="flex items-center mt-2 text-xs text-gray-500">
                <span className="bg-blue-50 text-blue-700 rounded-full px-2 py-0.5 mr-2">Éducatif</span>
                <span>courses.mit.edu</span>
                <span className="mx-2">•</span>
                <span>Français</span>
              </div>
            </div>
          </div>
        </div>
        
        <div className="p-4 hover:bg-gray-50 cursor-pointer">
          <div className="flex items-start">
            <div className="h-10 w-10 rounded bg-purple-100 flex items-center justify-center mr-3 mt-1">
              <Database className="h-5 w-5 text-purple-600" />
            </div>
            <div className="flex-1">
              <div className="flex justify-between">
                <h4 className="font-medium text-gray-900">Classes vs Interfaces: Guide complet</h4>
                <div className="flex">
                  <span className="bg-green-100 text-green-800 text-xs rounded-full px-2 py-0.5 flex items-center mr-1">
                    <Star className="h-3 w-3 mr-0.5" /> 4.6
                  </span>
                </div>
              </div>
              <p className="text-sm text-gray-600 line-clamp-2 mt-1">Différences fondamentales entre les classes et les interfaces en Java, avec des cas d'utilisation pratiques.</p>
              <div className="flex items-center mt-2 text-xs text-gray-500">
                <span className="bg-purple-50 text-purple-700 rounded-full px-2 py-0.5 mr-2">Commercial</span>
                <span>oracle.com</span>
                <span className="mx-2">•</span>
                <span>Anglais (traduction disponible)</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <div className="p-4 bg-gray-50 border-t">
        <div className="flex justify-between items-center">
          <button className="text-purple-600 hover:text-purple-800 text-sm font-medium flex items-center">
            <Filter className="h-4 w-4 mr-1" />
            Filtrer les résultats
          </button>
          <button 
            onClick={() => handlePhaseClick(3)} 
            className="bg-purple-600 hover:bg-purple-700 text-white text-sm font-medium py-2 px-4 rounded-lg flex items-center"
          >
            Analyser avec Aladinn
            <ArrowRight className="h-4 w-4 ml-1" />
          </button>
        </div>
      </div>
    </div>
  );
  
  // Intégrateur d'analyse sémantique (invisible)
  const renderIntentAnalysisIntegrator = () => (
    <IntentAnalysisIntegrator />
  );
  
  // Obtenir les phases avec l'état actif mis à jour
  const currentPhases = getPhasesWithActiveState();
  
  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 via-purple-50 to-white">
      {/* Intégrateur d'analyse sémantique */}
      {renderIntentAnalysisIntegrator()}
      
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Fil d'Ariane pour la navigation */}
        {activePhase > 0 && (
          <div className="mb-6">
            <nav className="flex items-center space-x-2 text-sm">
              <button
                onClick={() => navigate(`/inspiration/${competenceId}`)}
                className="text-purple-600 hover:text-purple-700 font-medium transition-colors"
              >
                Parcours d'inspiration
              </button>
              <span className="text-gray-400">/</span>
              <span className="text-gray-700 font-medium">
                {activePhase === 1 && 'Orientation'}
                {activePhase === 2 && 'Recherche 360°'}
                {activePhase === 3 && 'Synthèse'}
              </span>
            </nav>
          </div>
        )}

        {/* En-tête - style cohérent avec la landing page */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center p-3 bg-yellow-100 rounded-full mb-6">
            <Lightbulb className="h-8 w-8 text-yellow-600" />
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-6 leading-tight">
            Trouvez l'idée, le contact ou la solution qu'il vous faut
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Découvrez les meilleures ressources, fournisseurs, et solutions pour votre projet
          </p>
        </div>
        
        {/* Parcours d'inspiration avec les phases dynamiques */}
        <div className="mb-16">
          <h2 className="text-2xl font-bold text-center text-gray-800 mb-8">Votre parcours d'inspiration</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {currentPhases.map((phase) => (
              <PhaseCard
                key={phase.number}
                number={phase.number}
                title={phase.title}
                description={phase.description}
                icon={phase.icon}
                isActive={phase.isActive}
                onClick={() => handlePhaseClick(phase.number)}
              />
            ))}
          </div>
        </div>
        
        {/* Affichage conditionnel basé sur la phase active */}
        {activePhase === 2 && (
          <>
            {hasSemanticAnalysis ? (
              renderSearchResults()
            ) : (
              <div className="bg-white rounded-xl shadow-md p-6 mb-8 border border-gray-200 text-center">
                <MessageCircle className="h-12 w-12 text-purple-600 mx-auto mb-4" />
                <h3 className="text-xl font-semibold mb-2 text-gray-800">Analyse d'intention requise</h3>
                <p className="text-gray-600 mb-6">Pour obtenir des résultats pertinents, commencez par l'analyse de votre objectif</p>
                <button
                  onClick={() => handlePhaseClick(1)}
                  className="inline-flex items-center justify-center bg-purple-600 hover:bg-purple-700 text-white font-medium py-2.5 px-5 rounded-lg transition-colors"
                >
                  Commencer l'analyse
                  <ArrowRight className="ml-2 h-5 w-5" />
                </button>
              </div>
            )}
          </>
        )}
            
        {/* Avantages */}
        <div className="mb-16">
          <h2 className="text-2xl font-bold text-gray-900 mb-8 text-center">
            La puissance d'Aladinn pour l'inspiration
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {avantages.map((avantage, index) => (
              <AdvantageCard
                key={index}
                title={avantage.title}
                icon={avantage.icon}
                gradient={avantage.gradient}
                color={avantage.color}
                bgColor={avantage.bgColor}
                borderHover={avantage.borderHover}
                features={avantage.features}
              />
            ))}
          </div>
        </div>
        
        {activePhase === 3 && renderSearchResults()}
      </div>
    </div>
  );
};
 
export default InspirationSpace;
